import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import type {
  NativeStackNavigationProp,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ResponsiveGrid } from '../../components/ResponsiveGrid';
import { RoleSwitcher } from '../../components/RoleSwitcher';
import { ScreenHeader } from '../../components/ScreenHeader';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import {
  getAdminAttendanceDashboard,
  getHrAdminDashboard,
} from '../../services/attendanceService';
import { getUpcomingBirthdays } from '../../services/employeeService';
import type {
  AdminAttendanceDashboardMetrics,
  HrAdminDashboardData,
} from '../../types/attendance';
import type { DashboardScope } from '../../types/dashboard';
import type { UpcomingBirthday } from '../../types/employee';
import { canViewTeamDashboard } from '../../utils/approvalRoles';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import {
  addCalendarDays,
  formatCalendarDate,
  formatDate,
  getGreeting,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import {
  openDashboardCompensatoryWork,
  openDashboardLeaveDetails,
  openDashboardLeaveStatus,
  openDashboardPermissionDetails,
  openDashboardPermissionStatus,
  openDashboardWfhDetails,
  openDashboardWfhStatus,
} from '../../utils/hrAdminDashboardNavigation';
import {
  isApprovedWorkflowStatus,
  isPendingWorkflowActionStatus,
} from '../../utils/workflowStatus';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = (
  | NativeStackScreenProps<RootStackParamList, 'AdminDashboard'>
  | BottomTabScreenProps<MainTabParamList, 'AdminDashboard'>
  | NativeStackScreenProps<RootStackParamList, 'TeamDashboard'>
  | BottomTabScreenProps<MainTabParamList, 'TeamDashboard'>
) & {
  /**
   * `organization` (default) is the HR Admin view. `team` renders the same
   * dashboard for a Team Lead; the backend limits every figure and list to
   * the signed-in lead's assigned employees.
   */
  scope?: DashboardScope;
};

type MetricKey = keyof AdminAttendanceDashboardMetrics;

type MetricTone = 'primary' | 'success' | 'danger' | 'warning' | 'info';

type MetricDef = {
  key: MetricKey;
  label: string;
  tone: MetricTone;
  /** When true, the label uses the accent color (Present Today). */
  accentLabel?: boolean;
  icon: 'person' | 'briefcase' | 'home' | 'late' | 'clock';
};

/** Status chip labels accepted by AttendanceReportScreen. */
const METRIC_STATUS: Partial<Record<MetricKey, string>> = {
  presentToday: 'Present',
  absent: 'LOP',
  onLeave: 'On Leave',
  wfh: 'WFH',
  lateArrivals: 'Late',
  shortHours: 'Short Hours',
  missingCheckout: 'Missing Checkout',
};

/** Ignore extra tab focuses while a dashboard load is in flight or just finished. */
const FOCUS_RELOAD_COOLDOWN_MS = 2000;

/**
 * Admin dashboard cards. Early exit is intentionally not listed —
 * early exit must not create UI; missing checkout (did not exit) does.
 */
const METRICS: readonly MetricDef[] = [
  { key: 'totalEmployees', label: 'Total Employees', tone: 'info', icon: 'person' },
  {
    key: 'presentToday',
    label: 'Present Today',
    tone: 'success',
    accentLabel: true,
    icon: 'person',
  },
  { key: 'absent', label: 'LOP', tone: 'danger', icon: 'person' },
  { key: 'onLeave', label: 'On Leave', tone: 'warning', icon: 'briefcase' },
  { key: 'wfh', label: 'WFH', tone: 'info', icon: 'home' },
  { key: 'lateArrivals', label: 'Late Arrivals', tone: 'warning', icon: 'late' },
  { key: 'shortHours', label: 'Short Hours', tone: 'warning', icon: 'clock' },
  {
    key: 'missingCheckout',
    label: 'Missing Checkout',
    tone: 'warning',
    icon: 'clock',
  },
] as const;

function toneColor(palette: ColorPalette, tone: MetricTone): string {
  switch (tone) {
    case 'success':
      return palette.success;
    case 'danger':
      return palette.error;
    case 'warning':
      return palette.warning;
    case 'info':
      return palette.info;
    default:
      return palette.primary;
  }
}

function formatMetric(value: number | null): string {
  if (value === null) return '—';
  return String(value);
}

function MetricIcon({
  icon,
  color,
}: {
  icon: MetricDef['icon'];
  color: string;
}) {
  if (icon === 'home') {
    return (
      <View
        style={[iconStyles.circle, { backgroundColor: `${color}18` }]}
        accessibilityElementsHidden
      >
        <View style={[iconStyles.homeRoof, { borderBottomColor: color }]} />
        <View style={[iconStyles.homeBody, { borderColor: color }]} />
      </View>
    );
  }
  if (icon === 'late') {
    return (
      <View
        style={[iconStyles.circle, { backgroundColor: `${color}18` }]}
        accessibilityElementsHidden
      >
        <Text style={[iconStyles.lateLetter, { color }]}>L</Text>
      </View>
    );
  }
  if (icon === 'clock') {
    return (
      <View
        style={[iconStyles.circle, { backgroundColor: `${color}18` }]}
        accessibilityElementsHidden
      >
        <View style={[iconStyles.clockRing, { borderColor: color }]}>
          <View style={[iconStyles.clockHand, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  if (icon === 'briefcase') {
    return (
      <View style={iconStyles.personRoot} accessibilityElementsHidden>
        <View style={[iconStyles.briefCase, { borderColor: color }]}>
          <View style={[iconStyles.briefHandle, { borderColor: color }]} />
        </View>
      </View>
    );
  }
  return (
    <View style={iconStyles.personRoot} accessibilityElementsHidden>
      <View style={[iconStyles.personHead, { borderColor: color }]} />
      <View style={[iconStyles.personBody, { borderColor: color }]} />
    </View>
  );
}

function MetricCard({
  def,
  value,
  palette,
  compact,
  onPress,
}: {
  def: MetricDef;
  value: number | null;
  palette: ColorPalette;
  compact: boolean;
  onPress: () => void;
}) {
  const accent = toneColor(palette, def.tone);
  const labelColor = def.accentLabel ? accent : palette.textSecondary;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${def.label}: ${formatMetric(value)}`}
      onPress={onPress}
    >
      <AppCard
        style={[styles.metricCard, compact ? styles.metricCardCompact : null]}
      >
        <View style={styles.metricHeader}>
          <MetricIcon icon={def.icon} color={accent} />
          <Text
            style={[styles.metricLabel, { color: labelColor }]}
            numberOfLines={2}
          >
            {def.label}
          </Text>
        </View>
        <Text
          style={[
            styles.metricValue,
            compact ? styles.metricValueCompact : null,
            { color: palette.textPrimary },
          ]}
        >
          {formatMetric(value)}
        </Text>
      </AppCard>
    </Pressable>
  );
}

function DateNavButton({
  direction,
  disabled,
  onPress,
  palette,
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const label =
    direction === 'left' ? 'Previous date' : 'Next date';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      hitSlop={8}
      onPress={onPress}
      style={[
        styles.dateNavButton,
        {
          borderColor: palette.border,
          backgroundColor: palette.surface,
          opacity: disabled ? 0.4 : 1,
        },
      ]}
    >
      <Text style={[styles.dateNavArrow, { color: palette.primary }]}>
        {direction === 'left' ? '‹' : '›'}
      </Text>
    </Pressable>
  );
}

function emptyActivity(): HrAdminDashboardData {
  const today = todayBusinessCalendarDate();
  return {
    fromDate: today,
    toDate: today,
    leave: [],
    wfh: [],
    permission: [],
    compensatoryWork: [],
  };
}

function displayEmployeeName(name: string | null | undefined) {
  return formatDisplayLabel(name, 'Employee unavailable');
}

function displayReason(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : 'Not available';
}

function isBirthdayOnCalendarDate(
  item: UpcomingBirthday,
  onDate: string,
): boolean {
  if (item.nextBirthday === onDate) return true;
  const birthMonthDay = item.dateOfBirth.slice(5, 10);
  const onMonthDay = onDate.slice(5, 10);
  return Boolean(birthMonthDay && onMonthDay && birthMonthDay === onMonthDay);
}

function ActivityRow({
  name,
  reason,
  statusLabel,
  palette,
  showDivider,
  onNamePress,
  onStatusPress,
}: {
  name: string | null | undefined;
  reason: string | null | undefined;
  statusLabel: string;
  palette: ColorPalette;
  showDivider: boolean;
  onNamePress?: () => void;
  onStatusPress?: () => void;
}) {
  const statusText = formatDisplayLabel(statusLabel, 'Status unavailable');
  return (
    <View
      style={[
        styles.activityRow,
        showDivider
          ? { borderBottomColor: palette.border, borderBottomWidth: 1 }
          : null,
      ]}
    >
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={displayEmployeeName(name)}
        disabled={!onNamePress}
        onPress={onNamePress}
        style={styles.activityName}
      >
        <Text
          style={[
            styles.activityNameText,
            {
              color: onNamePress ? palette.primary : palette.textPrimary,
              textDecorationLine: onNamePress ? 'underline' : 'none',
            },
          ]}
          numberOfLines={1}
        >
          {displayEmployeeName(name)}
        </Text>
      </Pressable>
      <Text
        style={[styles.activityReason, { color: palette.textSecondary }]}
        numberOfLines={2}
      >
        {displayReason(reason)}
      </Text>
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={statusText}
        disabled={!onStatusPress}
        onPress={onStatusPress}
        style={styles.activityStatus}
      >
        <Text
          style={[
            styles.activityStatusText,
            {
              color: onStatusPress ? palette.primary : palette.textSecondary,
              textDecorationLine: onStatusPress ? 'underline' : 'none',
            },
          ]}
          numberOfLines={1}
        >
          {statusText}
        </Text>
      </Pressable>
    </View>
  );
}

function BirthdayRow({
  name,
  birthdayDate,
  palette,
  showDivider,
}: {
  name: string;
  birthdayDate: string;
  palette: ColorPalette;
  showDivider: boolean;
}) {
  return (
    <View
      style={[
        styles.activityRow,
        showDivider
          ? { borderBottomColor: palette.border, borderBottomWidth: 1 }
          : null,
      ]}
    >
      <Text
        style={[styles.activityNameText, styles.activityName, { color: palette.textPrimary }]}
        numberOfLines={1}
      >
        {name}
      </Text>
      <Text
        style={[styles.activityReason, { color: palette.textSecondary }]}
        numberOfLines={1}
      >
        {formatCalendarDate(birthdayDate)}
      </Text>
    </View>
  );
}

function SectionCard({
  title,
  count,
  emptyMessage,
  children,
  palette,
  columns,
}: {
  title: string;
  count: number;
  emptyMessage: string;
  children: React.ReactNode;
  palette: ColorPalette;
  columns?: ReadonlyArray<{ label: string; style: object }>;
}) {
  const headers = columns ?? [
    { label: 'Name', style: styles.activityName },
    { label: 'Reason', style: styles.activityReason },
    { label: 'Status', style: styles.activityStatus },
  ];
  return (
    <View style={styles.section} accessibilityRole="summary">
      <View style={styles.sectionHeader}>
        <Text
          accessibilityRole="header"
          style={[styles.sectionTitle, { color: palette.textPrimary }]}
        >
          {title}
        </Text>
        <Text style={[styles.sectionCount, { color: palette.textSecondary }]}>
          {count}
        </Text>
      </View>
      <AppCard style={styles.sectionCard}>
        {count === 0 ? (
          <Text style={[styles.emptyText, { color: palette.textSecondary }]}>
            {emptyMessage}
          </Text>
        ) : (
          <>
            <View style={styles.columnHeader}>
              {headers.map(column => (
                <Text
                  key={column.label}
                  style={[
                    styles.columnHeaderText,
                    column.style,
                    { color: palette.textMuted },
                  ]}
                >
                  {column.label}
                </Text>
              ))}
            </View>
            {children}
          </>
        )}
      </AppCard>
    </View>
  );
}

export function AdminDashboardScreen({ navigation, scope = 'organization' }: Props) {
  const isTeam = scope === 'team';
  /** HR Admin requests stay unchanged; only the team view sends a scope. */
  const scopeParam = useMemo(
    () => (isTeam ? { scope: 'team' as const } : {}),
    [isTeam],
  );
  const auth = useAuth();
  const palette = useAppColors();
  const layout = useResponsiveLayout();
  const displayName = auth.user?.displayName || auth.user?.username || 'there';
  const today = todayBusinessCalendarDate();
  const [selectedDate, setSelectedDate] = useState(today);
  const [metrics, setMetrics] = useState<AdminAttendanceDashboardMetrics | null>(
    null,
  );
  const [activity, setActivity] = useState<HrAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [birthdays, setBirthdays] = useState<UpcomingBirthday[] | null>(null);
  const mountedRef = useRef(true);
  const fetchGeneration = useRef(0);
  const activityFetchGeneration = useRef(0);
  const birthdayFetchGeneration = useRef(0);
  const inFlight = useRef(false);
  const lastSuccessAt = useRef(0);

  const themed = useMemo(
    () => ({
      heroGreeting: {
        ...typography.caption,
        color: palette.white,
        opacity: 0.9,
      },
      heroName: {
        ...typography.screenTitle,
        color: palette.white,
      },
      heroDate: {
        ...typography.body,
        color: palette.white,
        opacity: 0.85,
      },
    }),
    [palette],
  );

  const openMetric = useCallback(
    (key: MetricKey) => {
      const stack =
        navigation as unknown as NativeStackNavigationProp<RootStackParamList>;
      const status = METRIC_STATUS[key];
      if (isTeam) {
        stack.navigate(
          routes.teamAttendanceReport,
          status ? { initialStatus: status } : undefined,
        );
        return;
      }
      if (key === 'totalEmployees') {
        stack.navigate(routes.employees as never);
        return;
      }
      stack.navigate(
        routes.attendanceReport,
        status ? { initialStatus: status } : undefined,
      );
    },
    [isTeam, navigation],
  );

  const loadActivity = useCallback(async (date?: string) => {
    const selected = date ?? selectedDate;
    const currentRequest = ++activityFetchGeneration.current;
    setActivityError(null);
    try {
      const activityResult = await getHrAdminDashboard({
        fromDate: selected,
        toDate: selected,
        ...scopeParam,
      });
      if (
        !mountedRef.current ||
        currentRequest !== activityFetchGeneration.current
      ) {
        return;
      }
      setActivity(activityResult);
      setSelectedDate(selected);
    } catch (err) {
      if (
        !mountedRef.current ||
        currentRequest !== activityFetchGeneration.current
      ) {
        return;
      }
      setActivityError(
        attendanceErrorMessage(
          err,
          'Unable to load approved request records.',
        ),
      );
    }
  }, [scopeParam, selectedDate]);

  const loadBirthdays = useCallback(async () => {
    const currentRequest = ++birthdayFetchGeneration.current;
    try {
      const today = todayBusinessCalendarDate();
      const items = await getUpcomingBirthdays({
        fromDate: today,
        ...scopeParam,
      });
      if (
        !mountedRef.current ||
        currentRequest !== birthdayFetchGeneration.current
      ) {
        return;
      }
      setBirthdays(items.filter(item => isBirthdayOnCalendarDate(item, today)));
    } catch {
      if (
        !mountedRef.current ||
        currentRequest !== birthdayFetchGeneration.current
      ) {
        return;
      }
      setBirthdays([]);
    }
  }, [scopeParam]);

  const load = useCallback(
    async (isRefresh = false) => {
      if (inFlight.current) return;
      const currentRequest = ++fetchGeneration.current;
      inFlight.current = true;
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      loadActivity().catch(() => undefined);
      loadBirthdays().catch(() => undefined);
      try {
        const data = await getAdminAttendanceDashboard({
          attendanceDate: todayBusinessCalendarDate(),
          ...scopeParam,
        });
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setMetrics(data.metrics);
        lastSuccessAt.current = Date.now();
      } catch (err) {
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setError(
          attendanceErrorMessage(
            err,
            isTeam
              ? 'Unable to load the team attendance dashboard.'
              : 'Unable to load the admin attendance dashboard.',
          ),
        );
      } finally {
        if (currentRequest === fetchGeneration.current) {
          inFlight.current = false;
          if (mountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      }
    },
    [isTeam, loadActivity, loadBirthdays, scopeParam],
  );

  const shiftDate = useCallback(
    (days: number) => {
      const nextDate = addCalendarDays(selectedDate, days);
      if (!nextDate) return;
      setSelectedDate(nextDate);
      loadActivity(nextDate).catch(() => undefined);
    },
    [selectedDate, loadActivity],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (
        lastSuccessAt.current > 0 &&
        Date.now() - lastSuccessAt.current < FOCUS_RELOAD_COOLDOWN_MS
      ) {
        return;
      }
      load().catch(() => undefined);
    }, [load]),
  );

  const header = (
    <ScreenHeader bleed={false} right={<RoleSwitcher />}>
      <View style={styles.heroRow}>
        <View style={styles.heroText}>
          <Text style={themed.heroGreeting}>
            {isTeam ? `${getGreeting()} · My Team` : getGreeting()}
          </Text>
          <Text accessibilityRole="header" style={themed.heroName}>
            {displayName}
          </Text>
          <Text style={themed.heroDate}>{formatDate(new Date())}</Text>
        </View>
        <ProfileAvatar name={displayName} size={56} />
      </View>
    </ScreenHeader>
  );

  const records = activity ?? emptyActivity();
  const isVisibleRequest = (item: {
    employeeName?: string | null;
    reason?: string | null;
    statusCode?: string | null;
  }) =>
    Boolean(item.employeeName?.trim()) &&
    Boolean(item.reason?.trim()) &&
    (isPendingWorkflowActionStatus(item.statusCode) ||
      isApprovedWorkflowStatus(item.statusCode));
  const visibleLeave = records.leave.filter(isVisibleRequest);
  const visibleWfh = records.wfh.filter(isVisibleRequest);
  const visiblePermission = records.permission.filter(isVisibleRequest);
  const visibleCompensatory = records.compensatoryWork.filter(isVisibleRequest);
  const todayBirthdays = (birthdays ?? []).filter(item =>
    isBirthdayOnCalendarDate(item, today),
  );
  const stackNavigation =
    navigation as unknown as NativeStackNavigationProp<RootStackParamList>;

  const allowed = isTeam
    ? canViewTeamDashboard(auth.roles, auth.permissions)
    : auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) &&
      auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW);
  if (!allowed) return <UnauthorizedScreen />;

  return (
    <>
      {loading && !metrics ? (
        <AppScreen contentStyle={styles.center}>
          <AppLoader message="Loading dashboard" />
        </AppScreen>
      ) : error && !metrics ? (
        <AppScreen contentStyle={styles.center}>
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : (
        <AppScreen
          scroll
          contentStyle={styles.screen}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
              tintColor={palette.primary}
              accessibilityLabel="Refresh dashboard"
            />
          }
        >
          {header}
          <View style={styles.body}>
            <ResponsiveGrid
              columns={layout.isCompact ? 2 : layout.gridColumns}
            >
              {METRICS.map(def => (
                <MetricCard
                  key={def.key}
                  def={def}
                  value={metrics?.[def.key] ?? null}
                  palette={palette}
                  compact={layout.isCompact}
                  onPress={() => openMetric(def.key)}
                />
              ))}
            </ResponsiveGrid>

            <AppCard style={styles.filterCard}>
              <View style={styles.dateNavRow}>
                <DateNavButton
                  direction="left"
                  disabled={!addCalendarDays(selectedDate, -1)}
                  onPress={() => shiftDate(-1)}
                  palette={palette}
                />
                <Text
                  accessibilityRole="text"
                  accessibilityLabel={formatCalendarDate(selectedDate)}
                  style={[styles.dateValue, { color: palette.textPrimary }]}
                >
                  {formatCalendarDate(selectedDate)}
                </Text>
                <DateNavButton
                  direction="right"
                  disabled={!addCalendarDays(selectedDate, 1)}
                  onPress={() => shiftDate(1)}
                  palette={palette}
                />
              </View>
              {activityError ? (
                <Text
                  accessibilityRole="alert"
                  style={[styles.filterError, { color: palette.error }]}
                >
                  {activityError}
                </Text>
              ) : null}
            </AppCard>

            {todayBirthdays.length > 0 ? (
            <SectionCard
              title="Birthdays"
              count={todayBirthdays.length}
              emptyMessage="No birthdays today."
              palette={palette}
              columns={[
                { label: 'Name', style: styles.activityName },
                { label: 'Birthday Date', style: styles.activityReason },
              ]}
            >
              {todayBirthdays.map((item, index) => (
                <BirthdayRow
                  key={item.employeeId}
                  name={item.employeeName}
                  birthdayDate={item.nextBirthday}
                  palette={palette}
                  showDivider={index < todayBirthdays.length - 1}
                />
              ))}
            </SectionCard>
            ) : null}
            <SectionCard
              title="Leave"
              count={visibleLeave.length}
              emptyMessage="No leave records for the selected date."
              palette={palette}
            >
              {visibleLeave.map((item, index) => (
                <ActivityRow
                  key={item.leaveApplicationId}
                  name={item.employeeName}
                  reason={item.reason}
                  statusLabel={item.statusLabel}
                  palette={palette}
                  showDivider={index < visibleLeave.length - 1}
                  onNamePress={() =>
                    openDashboardLeaveDetails(stackNavigation, item)
                  }
                  onStatusPress={() =>
                    openDashboardLeaveStatus(stackNavigation, item)
                  }
                />
              ))}
            </SectionCard>
            <SectionCard
              title="WFH"
              count={visibleWfh.length}
              emptyMessage="No WFH records for the selected date."
              palette={palette}
            >
              {visibleWfh.map((item, index) => (
                <ActivityRow
                  key={item.wfhRequestId}
                  name={item.employeeName}
                  reason={item.reason}
                  statusLabel={item.statusLabel}
                  palette={palette}
                  showDivider={index < visibleWfh.length - 1}
                  onNamePress={() =>
                    openDashboardWfhDetails(stackNavigation, item)
                  }
                  onStatusPress={() =>
                    openDashboardWfhStatus(stackNavigation, item)
                  }
                />
              ))}
            </SectionCard>
            {isTimePermissionEnabled() ? (<SectionCard
              title="Permission"
              count={visiblePermission.length}
              emptyMessage="No permission records for the selected date."
              palette={palette}
            >
              {visiblePermission.map((item, index) => (
                <ActivityRow
                  key={item.permissionRequestId}
                  name={item.employeeName}
                  reason={item.reason}
                  statusLabel={item.statusLabel}
                  palette={palette}
                  showDivider={index < visiblePermission.length - 1}
                  onNamePress={() =>
                    openDashboardPermissionDetails(stackNavigation, item)
                  }
                  onStatusPress={() =>
                    openDashboardPermissionStatus(stackNavigation, item)
                  }
                />
              ))}
            </SectionCard>) : null}
            <SectionCard
              title="Compensatory Work"
              count={visibleCompensatory.length}
              emptyMessage="No compensatory work records for the selected date."
              palette={palette}
            >
              {visibleCompensatory.map((item, index) => (
                <ActivityRow
                  key={item.compensatoryWorkRequestId}
                  name={item.employeeName}
                  reason={item.reason}
                  statusLabel={item.statusLabel}
                  palette={palette}
                  showDivider={index < visibleCompensatory.length - 1}
                  onNamePress={() =>
                    openDashboardCompensatoryWork(stackNavigation, item)
                  }
                  onStatusPress={() =>
                    openDashboardCompensatoryWork(stackNavigation, item)
                  }
                />
              ))}
            </SectionCard>
          </View>
        </AppScreen>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  screen: { paddingHorizontal: 0, paddingVertical: 0 },
  center: { justifyContent: 'center' },
  heroRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minWidth: 0,
  },
  heroText: { flex: 1, gap: spacing.xs, minWidth: 0 },
  body: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    gap: spacing.xl,
  },
  filterCard: { gap: spacing.sm },
  dateNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
  },
  dateValue: {
    ...typography.sectionTitle,
    flexGrow: 1,
    textAlign: 'center',
    minWidth: 0,
  },
  dateNavButton: {
    width: 44,
    height: 50,
    borderWidth: 1,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateNavArrow: {
    fontSize: 28,
    fontWeight: '600',
    lineHeight: 32,
  },
  filterError: { ...typography.caption },
  section: { gap: spacing.sm },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  sectionTitle: {
    ...typography.sectionTitle,
    fontSize: 18,
  },
  sectionCount: {
    ...typography.caption,
    fontWeight: '700',
  },
  sectionCard: {
    gap: 0,
    paddingVertical: spacing.sm,
  },
  emptyText: { ...typography.body, paddingVertical: spacing.sm },
  columnHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingBottom: spacing.sm,
    marginBottom: spacing.xs,
  },
  columnHeaderText: {
    ...typography.caption,
    fontWeight: '700',
  },
  activityRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  activityName: {
    flex: 1,
    minWidth: 0,
  },
  activityNameText: {
    ...typography.body,
    fontWeight: '700',
  },
  activityReason: {
    ...typography.body,
    flex: 2,
    minWidth: 0,
  },
  activityStatus: {
    flex: 1,
    minWidth: 0,
  },
  activityStatusText: {
    ...typography.body,
    fontWeight: '600',
  },
  metricCard: {
    gap: spacing.md,
    minHeight: 118,
    justifyContent: 'space-between',
    maxWidth: '100%',
  } as ViewStyle,
  metricCardCompact: {
    gap: spacing.sm,
    minHeight: 104,
  } as ViewStyle,
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  metricLabel: {
    ...typography.caption,
    flex: 1,
    fontWeight: '600',
    fontSize: 13,
  },
  metricValue: {
    ...typography.screenTitle,
    fontSize: 32,
    lineHeight: 38,
    fontWeight: '700',
  },
  metricValueCompact: {
    fontSize: 26,
    lineHeight: 32,
  },
});

const iconStyles = StyleSheet.create({
  personRoot: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  personHead: {
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
  },
  personBody: {
    width: 16,
    height: 10,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderWidth: 2,
    marginTop: 2,
  },
  circle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lateLetter: {
    fontSize: 14,
    fontWeight: '800',
  },
  clockRing: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 3,
  },
  clockHand: {
    width: 2,
    height: 5,
    borderRadius: 1,
  },
  homeRoof: {
    width: 0,
    height: 0,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginBottom: 1,
  },
  homeBody: {
    width: 12,
    height: 8,
    borderWidth: 2,
    borderTopWidth: 0,
  },
  briefCase: {
    width: 18,
    height: 12,
    borderWidth: 2,
    borderRadius: 2,
    alignItems: 'center',
  },
  briefHandle: {
    width: 8,
    height: 4,
    borderWidth: 2,
    borderBottomWidth: 0,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    marginTop: -4,
  },
});
